import { Trans } from '@lingui/react/macro';
import { Fragment, useEffect, useMemo } from 'react';

import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import {
  cleanFilter,
  decodeFilterFieldName,
  encodeFilterFieldName,
} from '@/components/filters/fieldName';
import { useAppForm } from '@/components/Form';
import InfoPopover from '@/components/InfoPopover';
import {
  type FilterOperatorValue,
  type QueryStateFilter,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { Link } from '@/components/typography';
import { useTableState } from '@/providers/TableState';

type Props = {
  open: boolean;
  onClose: () => void;
};

type FilterFormValues = Record<string, FilterOperatorValue | null | undefined>;

const isValidFilter = (filter: FilterOperatorValue | null | undefined) => {
  if (filter == null) return false;
  if ('operand' in filter) return filter.operand != null && filter.operand.length > 0;
  if ('min' in filter || 'max' in filter) return true;
  return false;
};

const DataTableAdvancedFiltersDialog = ({ open, onClose }: Props) => {
  const { queryState, updateQueryState } = useInstantSearchState();
  // Use declaration-order columns so the filter list stays stable regardless of how the
  // user has reordered the table's columns.
  const { originalColumns } = useTableState();

  const filterColumns = useMemo(
    () => originalColumns.filter((col) => col.filterComponent),
    [originalColumns],
  );

  const buildValues = useMemo(() => {
    const values: FilterFormValues = {};
    for (const column of filterColumns) {
      const existing = queryState.filters.find((filter) => filter.field === column.id);
      values[encodeFilterFieldName(column.id)] = existing ?? null;
    }
    return values;
  }, [filterColumns, queryState.filters]);

  const form = useAppForm({
    defaultValues: buildValues as FilterFormValues,
    onSubmit: ({ value }) => {
      const filters = Object.entries(value)
        .map(([key, filter]) => {
          const cleaned = cleanFilter(filter);
          if (!isValidFilter(cleaned)) return null;
          return {
            ...(cleaned as QueryStateFilter),
            field: decodeFilterFieldName(key),
          };
        })
        .filter(Boolean) as QueryStateFilter[];

      updateQueryState({ ...queryState, filters });
      onClose();
    },
  });

  useEffect(() => {
    if (open) {
      form.reset(buildValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClearAll = () => {
    for (const column of filterColumns) {
      form.setFieldValue(encodeFilterFieldName(column.id), null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-end justify-between gap-2">
            <DialogTitle>
              <div className="flex gap-2">
                <Trans>Advanced Filters</Trans>
                <InfoPopover
                  title={<Trans>Advanced Filter Logic</Trans>}
                  body={
                    <Trans>
                      Multiple filters are "AND"ed together. Multiple values within a filter are
                      "OR"ed, though some filters have an option to change them to "AND".
                      <br />
                      <br />
                      If a filter has an "Exclude" option, it will exclude all selected values from
                      the result set.
                    </Trans>
                  }
                />
              </div>
            </DialogTitle>
            <Link variant="caption1" onClick={handleClearAll}>
              <Trans>Clear All</Trans>
            </Link>
          </div>
        </DialogHeader>
        <form.AppForm>
          <div className="no-scrollbar flex h-full max-h-[60vh] flex-1 flex-col gap-6 overflow-y-auto">
            {filterColumns.map((column) => (
              <Fragment key={column.id}>
                {column.filterComponent?.({
                  compact: true,
                  name: column.id,
                  label:
                    typeof column.advancedFilter === 'object'
                      ? column.advancedFilter.title
                      : column.title,
                  infoPopover: column.infoPopover
                    ? {
                        title: column.title,
                        body: column.infoPopover,
                      }
                    : undefined,
                })}
              </Fragment>
            ))}
          </div>
          <DialogFooter>
            <DialogClose
              render={
                <Button size="large">
                  <Trans>Cancel</Trans>
                </Button>
              }
            />
            <form.SubmitButton size="large" variant="on-primary">
              <Trans>Apply</Trans>
            </form.SubmitButton>
          </DialogFooter>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
};

export default DataTableAdvancedFiltersDialog;
